import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppService } from 'src/app/app.service';
import { SafetyObservation } from 'src/app/models/safety-observation.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { NetworkedAccountsSelectorComponent } from 'src/app/shared/networked-accounts-selector/networked-accounts-selector.component';
import { NetworkedUsersSelectorComponent } from 'src/app/shared/networked-users-selector/networked-users-selector.component';
import { RBACService } from 'src/app/shared/rbac.service';
import { SitesSelectorComponent } from 'src/app/shared/sites-selector/sites-selector.component';
import { UtilsService } from 'src/app/shared/utils.service';
import { SafetyObservationsViewComponent } from '../safety-observations-view/safety-observations-view.component';
import { faHardHat } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { AssetsSelectorComponent } from '../../shared/assets-selector/assets-selector.component';

@Component({
  selector: 'app-safety-observations-edit',
  templateUrl: './safety-observations-edit.component.html',
  styleUrls: ['./safety-observations-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SafetyObservationsEditComponent implements OnInit {
  safety_observation: SafetyObservation = new SafetyObservation();

  selectedFiles: File[] = [];
  faHardHat = faHardHat;

  reportedDateTime: moment.Moment | null;

  // RBAC permission flags. Resolved once in ngOnInit() to avoid re-evaluating on every change detection cycle.
  // This dialog is opened from BOTH the account register and the site perspective, so editing is
  // allowed with EITHER the register-level or the site-level edit permission, mirroring
  // SafetyObservationsController@update which checks both with ANY semantics.
  canEdit: boolean = false;

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService, // Is used in view.
    public rbac: RBACService,
    private dialogRef: MatDialogRef<SafetyObservationsEditComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: any
  ) {}

  ngOnInit() {
    // Resolve RBAC permission flags.
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_REGISTERS_SAFETY_OBSERVATIONS,
        RBACService.RBAC_A.A_EDIT
      ),
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES_SAFETY_OBSERVATIONS,
        RBACService.RBAC_A.A_EDIT
      )
    ]);

    // Get the id of the safety observation.
    const safety_observation_id: number = Number(
      this.dialogData['safety_observation_id']
    );

    if (safety_observation_id) {
      // Get the safety observation
      this.getSafetyObservation(safety_observation_id);
    } else {
      // A new safety observation is being reported.
      const site_id: number = Number(this.dialogData['site_id']);
      if (site_id) {
        this.safety_observation.site_id = site_id;
      }
    }
  }

  /**
   * Get the safety observation data from the API.
   * @param safety_observation_id The safety observation id.
   */
  private getSafetyObservation(safety_observation_id: number) {
    this.api
      .makeRequest('get', `v2/safety-observations/${safety_observation_id}`)
      .then((response) => {
        // Store the safety observation information.
        this.safety_observation.apply(response);
        // Convert the unix "reported_at" timestamp to a moment date/time object only if valid.
        const reported_date = this.safety_observation.reported_date;

        if (reported_date && Number(reported_date) > 0) {
          this.reportedDateTime = moment.unix(Number(reported_date));
        } else {
          this.reportedDateTime = null;
        }
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Save or update an existing safety observation based on the entered form data.
   * @param {NgForm} form - The form data.
   * @param {boolean} [closeEditDialogAfterUpdate=true] - Indicates whether to close the edit dialog after updating. Default is true.
   * @param {boolean} [continueWhenDone=false] - Indicates whether to continue after completion. Default is false.
   * @returns {Promise<void>}
   */
  async onSaveSafetyObservation(
    form: NgForm,
    closeEditDialogAfterUpdate: boolean = true,
    continueWhenDone: boolean = false
  ): Promise<void> {
    if (!form.valid) {
      this.utils.showFormValidationError();
      return;
    }

    const safetyObservationCopy = { ...this.safety_observation };

    // Get the relevant timezone to use.
    let timezone: string = this.app.account.timezone;
    if (typeof this.safety_observation.site.timezone != 'undefined') {
      // Use the associated site timezone instead.
      timezone = this.safety_observation.site.timezone;
    }

    // Convert the selected date and time to a unix timestamp.
    if (this.reportedDateTime && this.reportedDateTime instanceof moment) {
      const reportedDateTime: moment.Moment = moment.tz(
        this.reportedDateTime.format('M/D/YYYY, h:mm:ss a'),
        'M/D/YYYY, h:mm:ss a',
        timezone
      );
      safetyObservationCopy.reported_date = reportedDateTime.unix();
    } else {
      this.utils.showFormValidationError();
      return;
    }

    // check if the Safety Observation should be created or updated
    if (this.safety_observation.id) {
      await this.update(
        safetyObservationCopy as SafetyObservation,
        closeEditDialogAfterUpdate,
        continueWhenDone
      );
    } else {
      await this.create(
        safetyObservationCopy as SafetyObservation,
        continueWhenDone
      );
    }
  }

  /**
   * Creates a new safety observation and optionally continues when done.
   *
   * @param safety_observation The safety observation object to be created
   * @param continueWhenDone Flag indicating whether to continue when the operation is done, defaults to false
   * @return A Promise that resolves when the creation process is completed
   * @private
   */
  private create(
    safety_observation: SafetyObservation,
    continueWhenDone: boolean = false
  ): Promise<any> {
    return this.api
      .makeRequest('post', `v2/safety-observations`, safety_observation)
      .then(
        (response): void => {
          this.safety_observation.apply(response);
        }
      )
      .catch(
        (errorResponse): void => {
          this.utils.handleAPIErrors(errorResponse);
        }
      )
      .finally(
        (): void => {
          this.uploadSelectedFiles(this.safety_observation.id);

          // After create, only open the view dialog if user has edit permission (register OR site
          // level, matching the API). Create-only users should just have the dialog close.
          if (continueWhenDone && this.canEdit) {
            this.onViewSafetyObservation(this.safety_observation.id);
          }
          this.dialogRef.close();
        }
      );
  }

  /**
   * Updates a safety observation with the provided data.
   *
   * @param {SafetyObservation} safety_observation - The safety observation object to be updated.
   * @param closeEditAfterUpdateDialog
   * @param {boolean} continueWhenDone - Optional parameter to determine whether to continue with another action after updating. Default is false.
   * @returns {Promise<any>} - A Promise that resolves when the update operation is completed.
   *
   * @private
   */
  private update(
    safety_observation: SafetyObservation,
    closeEditAfterUpdateDialog: boolean = true,
    continueWhenDone: boolean = false
  ): Promise<any> {
    return this.api
      .makeRequest(
        'put',
        `v2/safety-observations/${safety_observation.id}`,
        safety_observation
      )
      .then(
        (response): void => {
          this.getSafetyObservation(this.safety_observation.id);
        }
      )
      .catch(
        (errorResponse): void => {
          this.utils.handleAPIErrors(errorResponse);
        }
      )
      .finally(
        (): void => {
          // If this is a new safety observation, open it in viewing mode.
          if (continueWhenDone) {
            this.onViewSafetyObservation(this.safety_observation.id);
          }
          if (closeEditAfterUpdateDialog || continueWhenDone) {
            this.dialogRef.close();
          }
        }
      );
  }

  /**
   * Uploads the selected files to the server for the safety observation.
   * Calls the API to make an upload request with the selected files.
   * Shows a success toast message after successful upload.
   * Clears the selected files array after completing the upload operation.
   *
   * @private
   * @returns {void}
   */
  private uploadSelectedFiles(id: number, callback?: any): void {
    if (id && this.selectedFiles.length > 0) {
      this.api
        .makeUploadRequest(
          `v2/file-manager/safety_observation/${id}`,
          this.selectedFiles
        )
        .then(
          (response: any): void => {
            this.utils.showToast('Your files successfully uploaded.');
          }
        )
        .finally(
          (): void => {
            this.selectedFiles.length = 0;
            if (typeof callback === 'function') {
              callback();
            }
          }
        );
    }
  }

  /**
   * View the safety observation.
   * @param safety_observation_id The id of the safety observation to view.
   */
  onViewSafetyObservation(safety_observation_id: number) {
    this.utils.showComponentDialog(
      SafetyObservationsViewComponent,
      {
        safety_observation_id: safety_observation_id
      },
      {
        width: '90%'
      }
    );
  }

  // [displayWith]="formatRatingLabel" on mat-slider.
  formatRatingLabel(rating: number) {
    switch (rating) {
      case 1:
        return 'Very Bad';
        break;
      case 2:
        return 'Bad';
        break;
      case 3:
        return 'Neutral';
        break;
      case 4:
        return 'Good';
        break;
      case 5:
        return 'Very Good';
        break;
      default:
        return '';
    }
  }

  /**
   * Select the site the safety observation should be associated with.
   */
  onSelectSite() {
    this.utils.showComponentDialog(
      SitesSelectorComponent,
      {
        selected: [this.safety_observation.site_id],
        multiple: false
      },
      {
        width: '1200px'
      },
      (selected_site_id: number) => {
        if (typeof selected_site_id != 'undefined') {
          this.safety_observation.site_id = selected_site_id;
        }
      }
    );
  }

  /**
   * Select contractors to associate with this safety observation.
   */
  onSelectContractors() {
    this.utils.showComponentDialog(
      NetworkedAccountsSelectorComponent,
      {
        selected: this.safety_observation.contractor_ids,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results: any) => {
        if (typeof results !== 'undefined') {
          // Set the newly selected ids.
          this.safety_observation.contractor_ids = results;
        }
      }
    );
  }

  /**
   * Select assets to associate with this safety observation.
   *
   * @return {void}
   */
  onSelectAssets(): void {
    this.utils.showComponentDialog(
      AssetsSelectorComponent,
      {
        selected: this.safety_observation.asset_ids,
        site_id: this.safety_observation.site_id,
        multiple: true
      },
      {
        width: '1700px'
      },
      (results: number[]) => {
        if (!results) {
          return;
        }
        this.safety_observation.asset_ids = results;
      }
    );
  }

  /**
   * Select users to associate with this safety observation.
   */
  onSelectUsers() {
    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        selected: this.safety_observation.user_ids,
        multiple: true,
        visitors_from_all_sites: true
      },
      {
        width: '1200px'
      },
      (results: any) => {
        if (typeof results !== 'undefined') {
          // Set the newly selected ids.
          this.safety_observation.user_ids = results;
        }
      }
    );
  }

  /**
   * If it is a new safety observation, add the files to the selected files array.
   * Existing safety observation files will be handled by the file manager.
   * @param fileList
   */
  onSelectFiles(fileList: FileList): void {
    const files: File[] = Array.from(fileList);
    if (files && files.length > 0) {
      this.selectedFiles.push(...files);
    }
  }

  /**
   * Select reporter to associate with this safety observation.
   */
  onSelectReporter() {
    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        multiple: false,
        selected: [this.safety_observation.reported_by],
        selectedAccountId: this.app.account.id,
        visitors_from_all_sites: true
      },
      {
        width: '1200px'
      },
      (userId: number) => {
        if (typeof userId == 'undefined') {
          return;
        }

        this.safety_observation.reported_by = userId;
      }
    );
  }

  /**
   * Retrieves the details of the output date and time based on the provided event.
   * @param {any} event - The event object containing the output date and time details.
   * @return {void}
   */
  getOutputDateTimeDetails(event: any) {
    this.reportedDateTime = event;
  }

  /**
   * Formats the safety observation rating value as a string.
   * @param {number | null} value - The rating value to be formatted.
   * @return {string} The formatted rating string, or an empty string if the value is null.
   */
  formatRating(value: number | null) {
    return value == null ? '' : `${value}/5`;
  }

  // Expose RBACService to template.
  protected readonly RBACService = RBACService;
}
